import { useAppStateStore } from '@client/stores/AppStateStore';
import { isFullPageRoute } from '@client/utils/route-presentation';
import type { AnyRoute } from '@tanstack/react-router';
import { useMatches } from '@tanstack/react-router';
import { createPortal } from 'react-dom';

type PageManagerProps = {
    children?: React.ReactNode;
    route: AnyRoute;
};

/**
 * This component should be included as the wrapper of the page content.
 * It will handle the full page content and the overlay content.
 * @param children - The page content.
 * @param route - The route of the page.
 * @returns The portal of the page content.
 */
export function PageController({ children, route }: PageManagerProps) {
    const matches = useMatches();
    const { fullPageElement } = useAppStateStore();
    const staticData = route.options.staticData;

    const isFullPage = isFullPageRoute(staticData);

    if (!isFullPage) {
        return <>{children}</>;
    }

    // Find last match that is fullPage (presentation: 'fullPage')
    const lastFullPageRoute = matches.findLast((match) => isFullPageRoute(match.staticData));
    // Is the last full page route the same as the route passed in?
    const isLastFullPageRoute = lastFullPageRoute?.routeId === route?.id;

    if (isLastFullPageRoute && fullPageElement) {
        return createPortal(children, fullPageElement);
    } else {
        return <div className="hidden">{children}</div>;
    }
}
